import { useMemo, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  C, PageHeader, GlassCard, Input, Button, Badge, StatusDot, Skeleton,
  EmptyState, Modal,
} from "@coremonit/design-system";
import {
  FolderKanban, PlusCircle, Search, ShoppingCart, Package, CreditCard,
  ShieldCheck, Brain, Server, Boxes, Users2, ArrowUpRight, Activity,
  AlertTriangle, Rocket,
  type LucideIcon,
} from "lucide-react";
import { useProjects, useProjectsSummary, useCreateProject } from "../api/hooks";
import type { Project, ProjectCreateInput } from "@coremonit/types";

// A tiny name → icon registry so mock (and later real) projects can pick from
// a curated palette without shipping every lucide icon.
const ICON_MAP: Record<string, LucideIcon> = {
  ShoppingCart, Package, CreditCard, ShieldCheck, Brain, Server, Boxes, FolderKanban,
};

const COLORS = ["#2D7FF9", "#10B981", "#F59E0B", "#A855F7", "#06B6D4", "#EF4444", "#EC4899"];

export function ProjectsPage() {
  const list      = useProjects();
  const summary   = useProjectsSummary();
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(searchParams.get("status") ?? "");
  const [creating, setCreating] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    setStatus(searchParams.get("status") ?? "");
  }, [searchParams]);

  const items = useMemo(() => {
    let all = list.data?.items ?? [];
    if (status) all = all.filter((p) => p.status === status);
    if (!q) return all;
    const needle = q.toLowerCase();
    return all.filter((p) => p.name.toLowerCase().includes(needle) || p.description.toLowerCase().includes(needle) || p.key.toLowerCase().includes(needle));
  }, [list.data, q, status]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PageHeader
        icon={<FolderKanban size={16} />}
        title="Projects"
        subtitle={summary.data ? `${summary.data.totals.projects} projects · ${summary.data.totals.services} services · ${summary.data.totals.members} members` : "Loading…"}
        badge={summary.data && summary.data.byStatus.critical > 0 ? <Badge color="red">{summary.data.byStatus.critical} CRITICAL</Badge> : undefined}
        actions={<Button variant="glow" icon={<PlusCircle size={12} />} onClick={() => setCreating(true)}>New project</Button>}
      />

      {/* Rollups */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
        <MiniStat label="Projects"     icon={<FolderKanban size={11} />} value={summary.data?.totals.projects  ?? "—"} color={C.blue}   />
        <MiniStat label="Services"     icon={<Boxes size={11} />}         value={summary.data?.totals.services ?? "—"} color={C.cyan}   />
        <MiniStat label="Members"      icon={<Users2 size={11} />}        value={summary.data?.totals.members  ?? "—"} color={C.purple} />
        <MiniStat label="Open incidents" icon={<AlertTriangle size={11} />} value={summary.data?.totals.incidents ?? "—"} color={summary.data && summary.data.totals.incidents > 0 ? C.amber : C.green} />
      </div>

      {/* Search + status filter */}
      <GlassCard style={{ padding: 12 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <Input value={q} onChange={setQ} prefix={<Search size={13} />} placeholder="Search projects by name, key, description…" />
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            {(["", "healthy", "warning", "critical", "unknown"] as const).map((s) => (
              <button
                key={s || "all"}
                onClick={() => setStatus(s)}
                style={{
                  padding: "6px 10px", borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: "pointer",
                  background: status === s ? C.blueDim : C.surfaceHigh,
                  border: `1px solid ${status === s ? C.borderBright : C.border}`,
                  color: status === s ? C.blue : C.textMuted,
                }}
              >
                {s ? s.charAt(0).toUpperCase() + s.slice(1) : "All"}
              </button>
            ))}
          </div>
        </div>
      </GlassCard>

      {list.isLoading ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
          {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} height={200} radius={16} />)}
        </div>
      ) : items.length === 0 ? (
        <GlassCard style={{ padding: 32 }}>
          <EmptyState
            icon={<FolderKanban size={22} />}
            title={q ? "No projects match your search" : "No projects yet"}
            description={q ? "Try clearing the search." : "Create your first project to start grouping services and delegating access."}
            action={<Button variant="glow" icon={<PlusCircle size={12} />} onClick={() => setCreating(true)}>New project</Button>}
          />
        </GlassCard>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 12 }}>
          {items.map((p) => <ProjectTile key={p.id} p={p} onOpen={() => nav(`/projects/${p.key}`)} />)}
        </div>
      )}

      <CreateProjectModal open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

// ─── ProjectTile ─────────────────────────────────────────────────────────
export function ProjectTile({ p, onOpen }: { p: Project; onOpen: () => void }) {
  const Icon = ICON_MAP[p.icon] ?? Boxes;
  const statusColor =
    p.status === "critical" ? C.red :
    p.status === "warning"  ? C.amber :
    p.status === "unknown"  ? C.textMuted : C.green;

  return (
    <GlassCard onClick={onOpen} style={{ padding: 16 }} accent={`${p.color}55`}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <div
            style={{
              width: 40, height: 40, borderRadius: 12,
              background: `linear-gradient(135deg, ${p.color}, ${p.color}66)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: `0 6px 18px ${p.color}55`,
              flexShrink: 0,
            }}
          >
            <Icon size={18} color="#fff" />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ color: C.text, fontSize: 14, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
              <StatusDot status={p.status} size={7} />
            </div>
            <div style={{ color: C.textMuted, fontSize: 10, fontFamily: "ui-monospace, monospace" }}>{p.key}</div>
          </div>
        </div>
        <ArrowUpRight size={14} color={C.textMuted} />
      </div>

      <div style={{ color: C.textSub, fontSize: 11.5, lineHeight: 1.5, minHeight: 34, marginBottom: 12 }}>
        {p.description}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 10 }}>
        <TileStat label="Services" value={p.serviceCount} color={C.blue}   />
        <TileStat label="Groups"   value={p.groupCount}   color={C.cyan}   />
        <TileStat label="Members"  value={p.memberCount}  color={C.purple} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 10 }}>
        <TileStat label="p95 latency" value={`${p.metrics.latencyP95Ms} ms`} color={p.metrics.latencyP95Ms > 500 ? C.amber : C.textSub} />
        <TileStat label="Error rate"  value={`${p.metrics.errorRatePct.toFixed(2)}%`} color={p.metrics.errorRatePct > 1 ? C.red : C.green} />
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 6 }}>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {p.environments.map((e) => <Badge key={e} color={e === "production" ? "blue" : "muted"}>{e}</Badge>)}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, color: statusColor, fontSize: 10, fontWeight: 700 }}>
          <Activity size={10} /> {p.metrics.availabilityPct.toFixed(2)}%
        </div>
      </div>
    </GlassCard>
  );
}

function MiniStat({ label, value, color, icon }: { label: string; value: React.ReactNode; color: string; icon?: React.ReactNode }) {
  return (
    <GlassCard style={{ padding: 12 }}>
      <div style={{ color: C.textMuted, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
        {icon}{label}
      </div>
      <div style={{ color, fontSize: 20, fontWeight: 700, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </GlassCard>
  );
}

function TileStat({ label, value, color }: { label: string; value: React.ReactNode; color: string }) {
  return (
    <div style={{ background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 8, padding: 6 }}>
      <div style={{ color: C.textMuted, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.3, fontWeight: 700 }}>{label}</div>
      <div style={{ color, fontSize: 12, fontWeight: 700, marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );
}

// ─── Create project modal ────────────────────────────────────────────────
function CreateProjectModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateProject();
  const nav = useNavigate();
  const [form, setForm] = useState<ProjectCreateInput>({
    key: "", name: "", description: "", color: COLORS[0], icon: "Boxes",
    ownerEmail: "admin@coremonit.io",
  });
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    if (!form.name.trim())         return setErr("Name is required");
    if (!/^[a-z0-9-]+$/.test(form.key)) return setErr("Key must be lowercase letters, numbers, dashes");
    if (!form.ownerEmail.includes("@")) return setErr("Owner email is required");
    try {
      const res = await create.mutateAsync(form);
      onClose();
      nav(`/projects/${res.project.key}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to create project");
    }
  };

  const keyFromName = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New project"
      subtitle="Group services, manage access, and monitor a slice of your fleet in isolation."
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="glow" icon={<PlusCircle size={12} />} loading={create.isPending} onClick={submit}>Create project</Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Field label="Name">
          <Input
            value={form.name}
            onChange={(v) => setForm((f) => ({ ...f, name: v, key: keyFromName(v) }))}
            placeholder="Payments Platform"
          />
        </Field>
        <Field label="Key" hint="Used in URLs. Lowercase letters, numbers, dashes.">
          <Input value={form.key} onChange={(v) => setForm((f) => ({ ...f, key: v }))} placeholder="payments-platform" />
        </Field>
        <Field label="Description">
          <Input value={form.description ?? ""} onChange={(v) => setForm((f) => ({ ...f, description: v }))} placeholder="What this project is responsible for." />
        </Field>
        <Field label="Owner email">
          <Input value={form.ownerEmail} onChange={(v) => setForm((f) => ({ ...f, ownerEmail: v }))} placeholder="owner@company.com" />
        </Field>
        <Field label="Colour">
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {COLORS.map((c) => (
              <button
                key={c}
                onClick={() => setForm((f) => ({ ...f, color: c }))}
                style={{
                  width: 26, height: 26, borderRadius: 8,
                  background: c, cursor: "pointer",
                  border: form.color === c ? `2px solid ${C.text}` : `1px solid rgba(255,255,255,0.15)`,
                  boxShadow: form.color === c ? `0 0 12px ${c}` : undefined,
                }}
              />
            ))}
          </div>
        </Field>
        {err && (
          <div style={{ padding: "8px 10px", borderRadius: 10, background: C.redDim, color: C.red, fontSize: 11, fontWeight: 600 }}>
            {err}
          </div>
        )}
      </div>
    </Modal>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ color: C.textSub, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      {children}
      {hint && <div style={{ color: C.textMuted, fontSize: 10 }}>{hint}</div>}
    </div>
  );
}
